import { useMemo } from 'react'
import type { IntlShape } from 'react-intl'
import { useIntl } from 'react-intl'
import {
  APPLE_PAY_PAYMENT_TYPE,
  GOOGLE_PAY_PAYMENT_TYPE,
  STRIPE_PAYMENT_ELEMENT_TYPE,
  STRIPE_PAYMENT_ELEMENT_CARD_TYPE,
  STRIPE_PAYMENT_ELEMENT_GIROPAY_TYPE,
  STRIPE_PAYMENT_ELEMENT_IDEAL_TYPE,
  STRIPE_PAYMENT_ELEMENT_WECHATPAY_TYPE,
  STRIPE_PAYMENT_ELEMENT_PAYNOW_TYPE,
  STRIPE_PAYMENT_ELEMENT_GRABPAY_TYPE,
  BRAINTREE_PAYMENT_TYPE,
  PAYPAL_PAYMENT_TYPE,
  VELOCITY_PAYMENT_TYPE,
  KLARNA_PAYMENT_TYPE,
  RAZORPAY_PAYMENT_TYPE,
  GO_CARDLESS_PAYMENT_TYPE,
  FLYING_BLUE_PAYMENT_TYPE,
  AZUPAY_PAY_ID_PAYMENT_TYPE,
  STRIPE_TERMINAL_PAYMENT_TYPE,
  STRIPE_CUSTOM_PAYTO_TYPE,
} from 'constants/payment'

export function getPaymentMethodLabels(intl: IntlShape): Record<string, string> {
  return {
    [APPLE_PAY_PAYMENT_TYPE]: intl.formatMessage({
      id: 'vqxYmFIclluyOkC0kquG',
      defaultMessage: 'Apple Pay',
      description: 'Payment method label for Apple Pay on the checkout page',
    }),
    [GOOGLE_PAY_PAYMENT_TYPE]: intl.formatMessage({
      id: 'CsaKzJEdsZI5jpaaSMwV',
      defaultMessage: 'Google Pay',
      description: 'Payment method label for Google Pay on the checkout page',
    }),
    [STRIPE_PAYMENT_ELEMENT_TYPE]: intl.formatMessage({
      id: 'WISmjKyFPPioWpB2FBm3',
      defaultMessage: 'Stripe Payment Element',
      description: 'Payment method label for Stripe Payment Element on the checkout page',
    }),
    [STRIPE_PAYMENT_ELEMENT_CARD_TYPE]: intl.formatMessage({
      id: 'Up1Z21HYYKbHKtv_xnk4',
      defaultMessage: 'Credit or debit card',
      description: 'Payment method label for credit or debit card on the checkout page',
    }),
    [STRIPE_PAYMENT_ELEMENT_GIROPAY_TYPE]: intl.formatMessage({
      id: 'yaTk5lxVnyi4yUyrjsJU',
      defaultMessage: 'Giropay',
      description: 'Payment method label for Giropay on the checkout page',
    }),
    [STRIPE_PAYMENT_ELEMENT_IDEAL_TYPE]: intl.formatMessage({
      id: 'L1cVUDx47Wqd0V7GsX_B',
      defaultMessage: 'iDEAL',
      description: 'Payment method label for iDEAL on the checkout page',
    }),
    [STRIPE_PAYMENT_ELEMENT_WECHATPAY_TYPE]: intl.formatMessage({
      id: 'acYvAbJ7BuAr4zfpWfDK',
      defaultMessage: 'WeChat Pay',
      description: 'Payment method label for WeChat Pay on the checkout page',
    }),
    [STRIPE_PAYMENT_ELEMENT_PAYNOW_TYPE]: intl.formatMessage({
      id: 'AKmYCXaMs_FSLchUe4Ga',
      defaultMessage: 'PayNow',
      description: 'Payment method label for PayNow on the checkout page',
    }),
    [STRIPE_PAYMENT_ELEMENT_GRABPAY_TYPE]: intl.formatMessage({
      id: '0EPuzIcPpp7WFty6XI7x',
      defaultMessage: 'GrabPay',
      description: 'Payment method label for GrabPay on the checkout page',
    }),
    [BRAINTREE_PAYMENT_TYPE]: intl.formatMessage({
      id: 'g1PO5mbvMqyNKqNem1Cw',
      defaultMessage: 'PayPal',
      description: 'Payment method label for PayPal on the checkout page',
    }),
    [PAYPAL_PAYMENT_TYPE]: intl.formatMessage({
      id: 'g1PO5mbvMqyNKqNem1Cw',
      defaultMessage: 'PayPal',
      description: 'Payment method label for PayPal on the checkout page',
    }),
    [VELOCITY_PAYMENT_TYPE]: intl.formatMessage({
      id: 'nCQP74ZidDb4gDr3j_t7',
      defaultMessage: 'Velocity Points',
      description: 'Payment method label for Velocity Points on the checkout page',
    }),
    [KLARNA_PAYMENT_TYPE]: intl.formatMessage({
      id: 'NtZHTJLdDtB12UJbnNm8',
      defaultMessage: 'Klarna',
      description: 'Payment method label for Klarna on the checkout page',
    }),
    [RAZORPAY_PAYMENT_TYPE]: intl.formatMessage({
      id: '50Api_Ta0lCa-f0qVmEE',
      defaultMessage: 'Razorpay',
      description: 'Payment method label for Razorpay on the checkout page',
    }),
    [GO_CARDLESS_PAYMENT_TYPE]: intl.formatMessage({
      id: 'u_Uh6qmNCLZTWiSiG9G7',
      defaultMessage: 'PayTo',
      description: 'Payment method label for PayTo on the checkout page',
    }),
    [STRIPE_CUSTOM_PAYTO_TYPE]: intl.formatMessage({
      id: 'u_Uh6qmNCLZTWiSiG9G7',
      defaultMessage: 'PayTo',
      description: 'Payment method label for PayTo on the checkout page',
    }),
    [FLYING_BLUE_PAYMENT_TYPE]: intl.formatMessage({
      id: 'cLetBswdvto1LVsKYNeX',
      defaultMessage: 'Flyingblue',
      description: 'Payment method label for Flying Blue on the checkout page',
    }),
    [AZUPAY_PAY_ID_PAYMENT_TYPE]: intl.formatMessage({
      id: '4twNkHZpoPC2IEW6CGq1',
      defaultMessage: 'PayID',
      description: 'Payment method label for PayID on the checkout page',
    }),
    [STRIPE_TERMINAL_PAYMENT_TYPE]: intl.formatMessage({
      id: 'kviPkChdzNidhIHNNusv',
      defaultMessage: 'Stripe Terminal',
      description: 'Payment method label for Stripe Terminal on the checkout page',
    }),
  }
}

function usePaymentLabels() {
  const intl = useIntl()

  return useMemo(() => {
    const paymentMethodLabels = getPaymentMethodLabels(intl)

    const shadowBanUserErrorMessage = intl.formatMessage({
      id: '33Ho9BobNX7QuNx1GG4U',
      defaultMessage: 'Something went wrong with the payment',
      description: 'Error message shown to shadow-banned users during payment',
    })

    const refundDueToChangeOfMindReason = intl.formatMessage({
      id: 'i4eZAel4KGWsmRtsLDdM',
      defaultMessage: 'Change of Mind Within Refund Guarantee Period',
      description: 'Refund reason label for change of mind within refund guarantee period',
    })

    const refundToCreditReason = intl.formatMessage({
      id: 'VtCs0vR2qvLzfodgruxP',
      defaultMessage: 'Covid 19',
      description: 'Refund reason label for refund to credit due to Covid 19',
    })

    const refundToCreditComment = intl.formatMessage({
      id: '7IyJnrtLhQrvhqgn2EJg',
      defaultMessage: 'Self-serve by customer',
      description: 'Comment attached to self-serve refund requests by customer',
    })

    const refundBackToOriginComment = intl.formatMessage({
      id: 'KsIW-B0uosmneFSctdXC',
      defaultMessage: 'Self-serve by customer',
      description: 'Comment attached to self-serve refunds back to original payment method',
    })

    return {
      paymentMethodLabels,
      shadowBanUserErrorMessage,
      refundDueToChangeOfMindReason,
      refundToCreditReason,
      refundToCreditComment,
      refundBackToOriginComment,
    }
  }, [intl])
}

export default usePaymentLabels
